import { createSlice } from "@reduxjs/toolkit";

/*
 * Global playback state — shared by the page player and the persistent
 * mini-player. UI state only: the media element (owned by PlayerProvider)
 * is the source of truth for time; position here is a throttled mirror.
 * Deliberately NOT in the redux-persist whitelist — resume positions are
 * handled by ReusableFunctions/playbackPosition (localStorage per media URL).
 *
 * `current` item shape:
 * { classId, blockId, title, seriesTitle, mediaKind: 'audio'|'video',
 *   src, streamUrl, poster, href, durationHint }
 */

export const SPEED_STEPS = [1, 1.25, 1.5, 2];

const initialState = {
  current: null,
  playing: false,
  position: 0,
  duration: 0,
  speed: 1,
  muted: false,
  volume: 1,
  expanded: false,
  // The current class's own page player card is on screen — the mini-player
  // yields to it (mobile media-focus brief, 2026-10).
  cardOnScreen: false,
  queue: [], // up-next in the current series (same item shape)
};

const playerSlice = createSlice({
  name: "player",
  initialState,
  reducers: {
    trackLoaded(state, action) {
      state.current = action.payload.item;
      state.position = action.payload.startAt || 0;
      state.duration = action.payload.item?.durationHint || 0;
      state.playing = Boolean(action.payload.autoplay);
    },
    playingChanged(state, action) {
      state.playing = Boolean(action.payload);
    },
    positionChanged(state, action) {
      state.position = action.payload;
    },
    durationChanged(state, action) {
      state.duration = action.payload;
    },
    speedChanged(state, action) {
      state.speed = action.payload;
    },
    speedCycled(state) {
      const idx = SPEED_STEPS.indexOf(state.speed);
      state.speed = SPEED_STEPS[(idx + 1) % SPEED_STEPS.length];
    },
    mutedChanged(state, action) {
      state.muted = Boolean(action.payload);
    },
    volumeChanged(state, action) {
      state.volume = Math.max(0, Math.min(1, action.payload));
      if (state.volume > 0) state.muted = false;
    },
    expandedChanged(state, action) {
      state.expanded = Boolean(action.payload);
    },
    cardOnScreenChanged(state, action) {
      state.cardOnScreen = Boolean(action.payload);
    },
    queueChanged(state, action) {
      state.queue = action.payload || [];
    },
    playerCleared() {
      return initialState;
    },
  },
});

export const {
  trackLoaded,
  playingChanged,
  positionChanged,
  durationChanged,
  speedChanged,
  speedCycled,
  mutedChanged,
  volumeChanged,
  expandedChanged,
  cardOnScreenChanged,
  queueChanged,
  playerCleared,
} = playerSlice.actions;

export default playerSlice.reducer;
